import { ReactElement, ReactNode } from 'react';
import { ROUTES, routeFor } from '@/common/constants/routes';
import {
  BenefitExemptionType,
  BenefitUsageType,
  IBenefit,
  IBenefitProductType,
} from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { TOrderCategorySlug } from '@/common/integrations/menhir/employee/orders/orders.types';
import { IBenefitType } from '@/common/types/general';
import { SchoolsNetworkLink } from '@/web/components/SchoolsNetworkLink/SchoolsNetworkLink';
import {
  IconBabyCarriage,
  IconBookOpen,
  IconBus,
  IconCoverflexApp,
  IconCreditCard,
  IconDesktop,
  IconFamilyPants,
  IconGraduationCap,
  IconHandHoldingHeart,
  IconHeartPulse,
  IconIcons,
  IconMoneyBillTransfer,
  IconParking,
  IconPersonCane,
  IconPiggyBank,
  IconReimbursement,
  IconShapes,
  IconShieldPlus,
  IconSpa,
  IconSun,
  IconTicketSimple,
  IconUtensils,
} from '@coverflex-tech/hypernova';
import { ArrayUtils, TMoney } from '@coverflex-tech/hypernova-utils';

export const benefitUsageMethods: BenefitUsageType[] = [
  'app',
  'card',
  'reimbursement',
];

export const benefitTypeSlugMapping: Record<IBenefitType, IMenhirBenefitSlug> =
  {
    childcare_voucher: 'childcare-voucher',
    education_training: 'education-training',
    gym_fitness: 'gym-fitness',
    health_wellbeing: 'health-wellbeing',
    professional_training: 'professional-training',
    retirement_investment: 'retirement-investment',
    senior_expenses: 'senior-expenses',
    technology: 'technology',
    transportation: 'transportation',
    parking: 'parking',
    summer_camp: 'summer-camp',
    donation: 'donation',
    other_benefits: 'other-benefits',
    cashout: 'cashout',
    fringe: 'fringe',
    recreation: 'recreation',
    family_care_services: 'family-care-services',
    it_education: 'it-education',
    it_transportation: 'it-transportation',
    pension_funds: 'pension-funds',
    meals: 'meals',
    es_public_transport: 'es-public-transport',
    es_childcare: 'es-childcare',
  };

export const benefitsUsableInApp: IMenhirBenefitSlug[] = [
  'childcare-voucher',
  'retirement-investment',
];

export const benefitUsageIcons: Record<BenefitUsageType, ReactNode> = {
  app: <IconCoverflexApp />,
  card: <IconCreditCard />,
  reimbursement: <IconReimbursement />,
};

export const benefitIconMapper: Record<
  IMenhirBenefitSlug | Extract<TOrderCategorySlug, 'health' | 'meals'>,
  ReactElement
> = {
  'childcare-voucher': <IconTicketSimple />,
  'health-wellbeing': <IconSpa />,
  'gym-fitness': <IconHeartPulse />,
  'education-training': <IconBookOpen />,
  'senior-expenses': <IconPersonCane />,
  technology: <IconDesktop />,
  'professional-training': <IconGraduationCap />,
  transportation: <IconBus />,
  'retirement-investment': <IconPiggyBank />,
  parking: <IconParking />,
  'summer-camp': <IconSun />,
  donation: <IconHandHoldingHeart />,
  'other-benefits': <IconShapes />,
  health: <IconShieldPlus />,
  cashout: <IconMoneyBillTransfer />,
  fringe: <IconIcons />,
  recreation: <IconSun />,
  'family-care-services': <IconFamilyPants />,
  'it-transportation': <IconBus />,
  'it-education': <IconBookOpen />,
  'pension-funds': <IconPiggyBank />,
  meals: <IconUtensils />,
  'es-public-transport': <IconBus />,
  'es-childcare': <IconBabyCarriage />,
  'es-professional-development': <IconGraduationCap />,
};

export const benefitProductTypeMethodMapping: Record<
  IBenefitProductType,
  BenefitUsageType
> = {
  apple_marketplace: 'app',
  card: 'card',
  childcare_voucher: 'app',
  reimbursement: 'reimbursement',
  retirement_investment: 'app',
  donation: 'app',
  voucher: 'app',
  cashout: 'app',
  voucher_card: 'card',
  contribution: 'app',
  health: 'app',
};

export const benefitDetailsOrder: BenefitUsageType[] = [
  'app',
  'card',
  'reimbursement',
];

export const getBenefitMethods = (benefit: IBenefit): BenefitUsageType[] => {
  // Special temporary case for es professional development
  if (benefit.slug === 'es-professional-development') {
    return ['app'];
  }

  const benefitMethods = ArrayUtils.distinct(
    benefit.products.map(
      (product) => benefitProductTypeMethodMapping[product.type]
    )
  );
  benefitMethods.sort(
    (methodA, methodB) =>
      benefitDetailsOrder.indexOf(methodA) -
      benefitDetailsOrder.indexOf(methodB)
  );
  return benefitMethods;
};

export const getBenefitExemptionsSlugs = (
  benefit: IBenefit
): BenefitExemptionType[] => {
  if (!benefit.exemptions) return [];
  return [
    benefit.exemptions.socialSecurity ? 'ss' : undefined,
    benefit.exemptions.valueAddedTax ? 'irs' : undefined,
  ].filter(Boolean) as BenefitExemptionType[];
};

export const benefitOptionsMapper: Record<
  IMenhirBenefitSlug | Extract<TOrderCategorySlug, 'health' | 'meals'>,
  {
    label: string;
    route: string;
  }
> = {
  'childcare-voucher': {
    label: 'dashboard.benefits.links.childcare',
    route: ROUTES.CHILDCARE_VOUCHER,
  },
  'health-wellbeing': {
    label: 'dashboard.benefits.links.health_wellbeing',
    route: ROUTES.BENEFIT_HEALTH,
  },
  'gym-fitness': {
    label: 'dashboard.benefits.links.gym_fitness',
    route: ROUTES.BENEFIT_GYM_FITNESS,
  },
  'education-training': {
    label: 'dashboard.benefits.links.education_training',
    route: ROUTES.BENEFIT_EDUCATION_TRAINING,
  },
  'senior-expenses': {
    label: 'dashboard.benefits.links.senior_expenses',
    route: ROUTES.BENEFIT_SENIOR_EXPENSES,
  },
  technology: {
    label: 'dashboard.benefits.links.technology',
    route: ROUTES.BENEFIT_TECHNOLOGY,
  },
  'professional-training': {
    label: 'dashboard.benefits.links.professional-training',
    route: ROUTES.BENEFIT_PROFESSIONAL_TRAINING,
  },
  transportation: {
    label: 'dashboard.benefits.links.transportation',
    route: ROUTES.BENEFIT_TRANSPORTATION,
  },
  'retirement-investment': {
    label: 'dashboard.benefits.links.retirement_investment',
    route: ROUTES.RETIREMENT_INVESTMENT,
  },
  parking: {
    label: 'dashboard.benefits.links.parking',
    route: ROUTES.BENEFIT_PARKING,
  },
  'summer-camp': {
    label: 'dashboard.benefits.links.summer_camp',
    route: ROUTES.BENEFIT_SUMMER_CAMP,
  },
  donation: {
    label: 'dashboard.benefits.links.donations',
    route: ROUTES.BENEFIT_DONATIONS,
  },
  'other-benefits': {
    label: 'dashboard.benefits.links.other_benefits',
    route: ROUTES.BENEFIT_OTHER_BENEFITS,
  },
  health: {
    label: 'dashboard.benefits.links.health',
    route: ROUTES.BENEFIT_HEALTH,
  },
  cashout: {
    label: 'dashboard.benefits.links.cashout',
    route: ROUTES.BENEFIT_CASHOUT,
  },
  fringe: {
    label: 'dashboard.benefits.links.fringe',
    route: routeFor(ROUTES.WELFARE_BENEFIT, 'fringe'),
  },
  recreation: {
    label: 'dashboard.benefits.links.recreation',
    route: routeFor(ROUTES.WELFARE_BENEFIT, 'recreation'),
  },
  'family-care-services': {
    label: 'dashboard.benefits.links.family_care_services',
    route: routeFor(ROUTES.WELFARE_BENEFIT, 'family-care-services'),
  },
  'it-education': {
    label: 'dashboard.benefits.links.it_education',
    route: routeFor(ROUTES.WELFARE_BENEFIT, 'it-education'),
  },
  'it-transportation': {
    label: 'dashboard.benefits.links.it_transportation',
    route: routeFor(ROUTES.WELFARE_BENEFIT, 'it-transportation'),
  },
  'pension-funds': {
    label: 'dashboard.benefits.links.pension_funds',
    route: routeFor(ROUTES.WELFARE_BENEFIT, 'pension-funds'),
  },
  meals: {
    label: 'dashboard.benefits.links.meals',
    route: routeFor(ROUTES.BENEFIT_DETAIL, 'meals'),
  },
  'es-public-transport': {
    label: 'dashboard.benefits.links.es_public_transport',
    route: routeFor(ROUTES.BENEFIT_DETAIL, 'es-public-transport'),
  },
  'es-childcare': {
    label: 'dashboard.benefits.links.es_childcare',
    route: routeFor(ROUTES.BENEFIT_DETAIL, 'es-childcare'),
  },
  'es-professional-development': {
    label: 'dashboard.benefits.links.es_professional_development',
    route: routeFor(ROUTES.BENEFIT_DETAIL, 'es-professional-development'),
  },
};

export const getBenefitHeaderActions = (benefit: IBenefit) => {
  if (benefit.slug === 'education-training') {
    const voucherProduct = benefit.products.find(
      (product) => product.type === 'voucher'
    );
    if (voucherProduct) {
      return <SchoolsNetworkLink context="education_page" />;
    }
  }

  return null;
};

export function getPercentageOfUsage(available: TMoney, limit: TMoney): number {
  return (available.amount / limit.amount) * 100;
}

export const benefitRouteTypeMapping: Record<string, IBenefitType> = {
  'health-wellbeing': 'health_wellbeing',
  'gym-fitness': 'gym_fitness',
  'education-training': 'education_training',
  'senior-expenses': 'senior_expenses',
  technology: 'technology',
  transportation: 'transportation',
  'professional-training': 'professional_training',
  parking: 'parking',
  'summer-camp': 'summer_camp',
  'other-benefits': 'other_benefits',
  recreation: 'recreation',
  fringe: 'fringe',
  'it-education': 'it_education',
  'it-transportation': 'it_transportation',
  'family-care-services': 'family_care_services',
  'pension-funds': 'pension_funds',
};
